import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, throwError } from 'rxjs';
import { GridSettings } from '../../../shared/model/grid-settings.interface';
import { RestApiModel } from '../../../shared/model/rest-api.model';
import { SortUtils } from '../../../shared/utils/sort.utils';
import { AuthService } from '../../security/service/auth.service';
import { PartsSupplierPartModel } from '../model/part-supplier-parts.model';
import { PartModel } from '../model/part.model';
import { PartsSearchModel } from '../model/parts-search.model';

/**
 * @name PartsService
 */
@Injectable()
export class PartsService {
  constructor(private http: HttpClient, private authService: AuthService) {}
  /**
   * Get the model that stores parts search filters.
   * @returns {PartsSearchModel}
   */
  getPartsSearchInput(): PartsSearchModel {
    if (localStorage.getItem('_partsSearch')) {
      return JSON.parse(localStorage.getItem('_partsSearch'));
    } else {
      return new PartsSearchModel();
    }
  }

  /**
   * Set the model to store parts search filters.
   * @param {PartsSearchModel} searchModel the search model for parts
   */
  setPartsSearchInput(searchModel: PartsSearchModel) {
    localStorage.setItem('_partsSearch', JSON.stringify(searchModel));
  }

  /**
   * Retrieve Product Lines for parts
   * @returns {Observable<Array<any>>}
   */
  getProductLines(): Observable<Array<any>> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/productLines')
      .map((res: any) => {
        return res.results
          ? res.results.sort((a: any, b: any) =>
              SortUtils.compareByProperty(a, b, 'name')
            )
          : null;
      })
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retrieve makes for parts
   * @returns {Observable<Array><any>>}
   */
  getMakes(): Observable<Array<any>> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/makes')
      .map((res: any) => {
        return res.results
          ? res.results.sort((a: any, b: any) =>
              SortUtils.compareByProperty(a, b, 'name')
            )
          : null;
      })
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retrieve part types for parts
   * @returns {Observable<Array<any>>}
   */
  getPartTypes(): Observable<Array<any>> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/partTypes')
      .map((res: any) => {
        return res.results
          ? res.results.sort((a: any, b: any) =>
              SortUtils.compareByProperty(a, b, 'name')
            )
          : null;
      })
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retrieve Part Classifications for parts
   * @returns {Observable<Array<any>>}
   */
  getPartClassifications(): Observable<Array<any>> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/partClassifications')
      .map((res: any) => {
        return res.results
          ? res.results.sort((a: any, b: any) =>
              SortUtils.compareByProperty(a, b, 'name')
            )
          : null;
      })
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retrieve part categories for parts
   * @returns {Observable<Array<any>>}
   */
  getPartCategories(): Observable<Array<any>> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/partCategories')
      .map((res: any) => {
        return res.results
          ? res.results.sort((a: any, b: any) =>
              SortUtils.compareByProperty(a, b, 'name')
            )
          : null;
      })
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retrieve equipment types for parts
   * @returns {Observable<Array<any>>}
   */
  getEquipmentTypes(): Observable<Array<any>> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/partEquipmentTypes')
      .map((res: any) => {
        return res.results
          ? res.results.sort((a: any, b: any) =>
              SortUtils.compareByProperty(a, b, 'name')
            )
          : null;
      })
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retrieve all lines of service
   * @returns {Observable<Array<any>>}
   */
  getLinesOfService = (): Observable<Array<any>> => {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/linesOfService')
      .map((res: any) => res)
      .catch(error => {
        return throwError(error);
      });
  };

  /**
   * Retrieve lines of service for a part
   * @param {string} partId the id of the part
   * @param {boolean} inactive return inactive lines of service or not
   * @returns {Observable<Array<any>>}
   */
  getPartLinesOfService(
    partId: string,
    inactive: boolean
  ): Observable<Array<any>> {
    return this.http
      .get(
        RestApiModel.PROXY_API_URL +
          `/part/${partId}/lineOfServices?showInactive=${inactive}`
      )
      .map((res: any) => {
        return res.results
          ? res.results.sort((a: any, b: any) =>
              SortUtils.compareByProperty(a, b, 'name')
            )
          : null;
      })
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retrieve lines of service for a part
   * @param {Array<any>} newPut lines of service added to the part with an existing relationship
   * @param {Array<any>} newPost lines of service added to the part as a new relationship
   * @param {Array<any>} deletedPut lines of service removed from the part
   * @param {string} partId the id of the part to update
   * @returns {Observable<Array<any>>}
   */
  updatePartLinesOfService(
    newPut: Array<any>,
    newPost: Array<any>,
    deletedPut: Array<any>,
    partId: string
  ): Observable<Array<any>> {
    let responses = [];
    newPut.map(item => {
      responses.push(this.updatePartLineOfService(partId, item.id, true));
    });
    deletedPut.map(item => {
      responses.push(this.updatePartLineOfService(partId, item.id, false));
    });
    newPost.map(item => {
      responses.push(this.addPartLineOfService(partId, item.id));
    });
    return forkJoin(responses);
  }

  /**
   * Updates to active or inactive a line of service tied to a part
   * @param {string} partId the id of the part
   * @param {string} lineOfServiceId the id of the line of service
   * @param {boolean} isActive whether the part line of service should be active or not
   * @returns {Observable<Array<any>>}
   */
  updatePartLineOfService(
    partId: string,
    lineOfServiceId: string,
    isActive: boolean
  ): Observable<any> {
    return this.http
      .put(
        RestApiModel.PROXY_API_URL +
          `/part/${partId}/lineOfService/${lineOfServiceId}`,
        { isActive: isActive }
      )
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * adds a line of service to a part
   * @param {string} partId the id of the part
   * @param {string} lineOfServiceId the id of the line of service
   * @returns {Observable<Array<any>>}
   */
  addPartLineOfService(
    partId: string,
    lineOfServiceId: string
  ): Observable<any> {
    return this.http
      .post(
        RestApiModel.PROXY_API_URL +
          `/part/${partId}/lineOfService/${lineOfServiceId}`,
        null
      )
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retrieve supplier info for the part
   * @param {string} partId the id of the part
   * @returns {Observable<Array<any>>}
   */
  getPartSupplierParts(partId: string): Observable<Array<any>> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + `/part/${partId}/supplierParts`)
      .map((res: any) => {
        return res.results
          ? res.results.sort((a: any, b: any) =>
              SortUtils.compareByProperty(a, b, 'name')
            )
          : null;
      })
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retrieve specific supplier info for the part
   * @param {string} partId the id of the part
   * @param {string} supplierId the id of the supplier
   * @returns {Observable<any>}
   */
  getPartSupplierPart(partId: string, supplierId: string): Observable<any> {
    return this.http
      .get(
        RestApiModel.PROXY_API_URL +
          `/part/${partId}/supplierPart/${supplierId}`
      )
      .map((res: any) => {
        return res.results;
      })
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retrieve supplier names from list of supplier Ids
   * @param {Array<any>} suppliers the list of suppliers to get
   * @returns {Observable<Array<any>>}
   */
  getAllSupplierNames(suppliers: Array<any>) {
    let responses = [];
    suppliers.map(supplier => {
      responses.push(this.getSupplier(supplier.supplierId));
    });
    return forkJoin(responses);
  }

  /**
   * Retrieve supplier name
   * @param {string} partId the id of the part
   * @returns {Observable<Array<any>>}
   */
  getSupplier(supplierId: string): Observable<any> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + `/supplier/${supplierId}`)
      .map((res: any) => {
        return res.results;
      })
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retrieve list of all suppliers
   * @returns {Observable<Array<any>>}
   */
  getSuppliers(): Observable<any> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/suppliers')
      .map((res: any) => {
        return res.results
          ? res.results.sort((a: any, b: any) =>
              SortUtils.compareByProperty(a, b, 'name')
            )
          : null;
      })
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Updates supplier part information
   * @param {string} partId the id of the part
   * @param {string} supplierId the id of the supplier
   * @param {PartsSupplierPartModel} supplierPart sku, cost, and isActive model for supplier part
   * @returns {Observable<Array<any>>}
   */
  updatePartsSupplierPart(
    partId: string,
    supplierId: string,
    supplierPart: PartsSupplierPartModel
  ): Observable<any> {
    return this.http
      .put(
        RestApiModel.PROXY_API_URL +
          `/part/${partId}/supplierPart/${supplierId}`,
        supplierPart
      )
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * adds supplier part information
   * @param {string} partId the id of the part
   * @param {string} supplierId the id of the supplier
   * @param {PartsSupplierPartModel} supplierPart sku, cost, and isActive model for supplier part
   * @returns {Observable<Array<any>>}
   */
  addPartsSupplierPart(
    partId: string,
    supplierId: string,
    supplierPart: PartsSupplierPartModel
  ): Observable<any> {
    return this.http
      .post(
        RestApiModel.PROXY_API_URL +
          `/part/${partId}/supplierPart/${supplierId}`,
        supplierPart
      )
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retrieve customer info for the part
   * @param {string} partId the id of the part
   * @returns {Observable<Array<any>>}
   */
  getCustomerSuppliedParts(partId: string): Observable<Array<any>> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + `/part/${partId}/customers`)
      .map((res: any) => {
        return res.results;
      })
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retrieve customer names from list of customer numbers
   * @param {Array<any>} customers the list of customers to get
   * @returns {Observable<Array<any>>}
   */
  getAllCustomerNames(customers: Array<any>) {
    let responses = [];
    customers.map(customer => {
      responses.push(this.getCustomer(customer.customerNumber));
    });
    return forkJoin(responses);
  }

  /**
   * Retrieve all customers
   * @returns {Observable<Array<any>>}
   */
  getCustomers = (): Observable<Array<any>> => {
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/customers')
      .map((res: any) => res)
      .catch(error => {
        return throwError(error);
      });
  };

  /**
   * Retrieve customer name
   * @param {string} partId the id of the part
   * @returns {Observable<Array<any>>}
   */
  getCustomer(customerNumber: string): Observable<any> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + `/customer/${customerNumber}`)
      .map((res: any) => {
        return res.results;
      })
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retrieve lines of service for a part
   * @param {Array<any>} newPut customers added to the part with an existing relationship
   * @param {Array<any>} newPost customers added to the part as a new relationship
   * @param {Array<any>} deletedPut customers removed from the part
   * @param {string} partId the id of the part to update
   * @returns {Observable<Array<any>>}
   */
  updateCustomerParts(
    newPut: Array<any>,
    newPost: Array<any>,
    deletedPut: Array<any>,
    partId: string
  ): Observable<Array<any>> {
    let responses = [];
    newPut.map(item => {
      responses.push(this.updateCustomerPart(partId, item.number, true));
    });
    deletedPut.map(item => {
      responses.push(this.updateCustomerPart(partId, item.number, false));
    });
    newPost.map(item => {
      responses.push(this.addCustomerPart(partId, item.number));
    });
    return forkJoin(responses);
  }

  /**
   * Updates to active or inactive a line of service tied to a part
   * @param {string} partId the id of the part
   * @param {string} lineOfServiceId the id of the line of service
   * @param {boolean} isActive whether the part line of service should be active or not
   * @returns {Observable<Array<any>>}
   */
  updateCustomerPart(
    partId: string,
    customerNumber: string,
    isActive: boolean
  ): Observable<any> {
    return this.http
      .put(
        RestApiModel.PROXY_API_URL +
          `/customer/${customerNumber}/part/${partId}`,
        { isActive: isActive }
      )
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * adds a line of service to a part
   * @param {string} partId the id of the part
   * @param {string} lineOfServiceId the id of the line of service
   * @returns {Observable<Array<any>>}
   */
  addCustomerPart(partId: string, customerNumber: string): Observable<any> {
    return this.http
      .post(
        RestApiModel.PROXY_API_URL +
          `/customer/${customerNumber}/part/${partId}`,
        null
      )
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Searches for parts based on the filter
   * @param {PartsSearchModel} searchModel the filters to search on
   * @returns {Observable<any>} an observable of the results returned
   */
  public searchParts(searchModel: PartsSearchModel): Observable<any> {
    const queryString = this.setPartsSearchQueryString(searchModel);
    return this.http
      .get(RestApiModel.PROXY_API_URL + '/parts' + queryString)
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Retires (sets inactive) an array of parts
   * @param {Array<PartModel>} parts list of parts to be retired
   * @returns {Observable<any>} an observable of the true or false result
   */
  public retireParts(parts: Array<PartModel>): Observable<any> {
    let responses = [];
    parts.map(part => {
      responses.push(this.retirePart(part.id));
    });
    // set a delay for more accurate results when refreshing grid
    return forkJoin(responses).delay(1000);
  }

  /**
   * Retires (sets inactive) a part
   * @param {number} id id of part to be retired
   * @returns {Observable<any>} an observable of the true or false result
   */
  public retirePart(id: number): Observable<any> {
    return this.http
      .delete(RestApiModel.PROXY_API_URL + `/part/${id}`)
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Activates a list of parts
   * @param {Array<PartModel>} parts
   * @returns {Observable<any>}
   */
  public activateParts(parts: Array<PartModel>): Observable<any> {
    let responses = [];
    parts.map(part => {
      responses.push(this.activatePart(part));
    });
    return forkJoin(responses).delay(1000);
  }

  /**
   * Activates a part
   * @param {PartModel} part
   * @returns {Observable<any>}
   */
  public activatePart(part: PartModel): Observable<any> {
    part.isActive = true;
    return this.updatePart(part);
  }

  /**
   * Updates a part
   * @param {any} part the part model to update
   * @returns {Observable<any>}
   */
  public updatePart(part: any): Observable<any> {
    delete part.skuAsHtml;
    delete part.links;
    delete part.name;
    return this.http
      .put(RestApiModel.PROXY_API_URL + `/part/${part.id}`, part)
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Creates a new part
   * @param {any} part the part model to create
   * @returns {Observable<any>}
   */
  public addPart(part: any): Observable<any> {
    delete part.name;
    return this.http
      .post(RestApiModel.PROXY_API_URL + '/part', part)
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Gets details for a part
   * @param {any} partId id of the part
   * @returns {Observable<any>}
   */
  public getPartDetails(partId: string): Observable<any> {
    return this.http
      .get(RestApiModel.PROXY_API_URL + `/part/${partId}`)
      .catch(error => {
        return throwError(error);
      });
  }

  /**
   * Gets in local storage grid settings like column order, column size, etc
   * @returns {GridSettings} the settings of the grid
   */
  public getStatePersistingInputs(): GridSettings {
    if (localStorage.getItem('_partsSearchGrid')) {
      return JSON.parse(localStorage.getItem('_partsSearchGrid'));
    } else {
      return null;
    }
  }

  /**
   * Sets in local storage grid settings like column order, column size, etc
   * @param {GridSettings} input the settings of the grid
   */
  public setStatePersistingInputs(input: GridSettings) {
    localStorage.setItem('_partsSearchGrid', JSON.stringify(input));
  }

  /**
   * Creates query string to send to get parts API based on filter paramater
   * @param {PartsSearchModel} searchModel the filters to search on
   * @returns {string} the query string
   */
  private setPartsSearchQueryString(searchModel: PartsSearchModel): string {
    let query = '';
    for (let prop in searchModel.filterModel) {
      if (
        searchModel.filterModel.hasOwnProperty(prop) &&
        searchModel.filterModel[prop] !== '' &&
        searchModel.filterModel[prop] !== null
      ) {
        if (prop === 'active') {
          if (searchModel.filterModel[prop] === 'Active') {
            query += prop + '=' + 'true' + '&';
          } else if (searchModel.filterModel[prop] === 'Inactive') {
            query += prop + '=' + 'false' + '&';
          }
        } else if (
          prop === 'number' ||
          prop === 'skuName' ||
          prop === 'keyword' ||
          prop === 'description' ||
          prop === 'name' ||
          prop === 'searchString'
        ) {
          query +=
            prop +
            '="' +
            searchModel.filterModel[prop].toString().trim() +
            '"&';
        } else {
          query +=
            prop + '=' + searchModel.filterModel[prop].toString().trim() + '&';
        }
      }
    }
    query = `?${query}pageNumber=${searchModel.pageNumber}&pageSize=${
      searchModel.pageSize ? searchModel.pageSize : '100'
    }`;

    return query;
  }

  /**
   * Returns true or false if the user logged in can manage assets
   * @returns {boolean} the query string
   */
  public canManageParts(): boolean {
    // VL-2482: Admin Portal part create/edit/activate/retire is disabled.
    return false;
  }
}
